// @system — route guard that redirects unauthenticated users to /auth
// Also redirects new users (onboarding not complete) to /onboarding.
// Integrates isRouteLocked() for subscription-based feature gating.
// Includes RouteErrorBoundary to catch child component crashes and redirect
// to /auth instead of showing a blank white page (#31871).
import { Component } from 'react'
import { Navigate, useLocation, Link } from 'react-router-dom'
import { useAuthContext } from '@/app/store/@system/auth'
import { usePermissions } from '@/app/hooks/@system/usePermissions'
import { roleSatisfies } from '@/app/lib/@system/roles'
import { isRouteLocked } from '@/app/routes/@system/utils'
import { isModuleEnabled } from '@/config/@system/modules'
import { text } from '@/config'
import { Spinner } from '../Loading'
import { EmailVerificationBanner } from '../EmailVerificationBanner'
import { Lock, AlertTriangle, RefreshCw, ShieldOff } from 'lucide-react'
import { Button } from '../ui/button'

/**
 * Shown when the signed-in user lacks the permission a route requires. A real
 * page (not a redirect) so staff understand why the link they followed is
 * closed to them instead of landing on a blank screen or the wrong dashboard.
 * @param {{ permission?: string }} props
 */
export function AccessDeniedPage({ permission }) {
  const t = text.admin.access
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center text-center px-4" role="alert" data-testid="access-denied">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-surface mb-6">
        <ShieldOff className="h-8 w-8 text-brand-text-muted" aria-hidden="true" />
      </div>
      <h1 className="text-2xl font-bold mb-2 text-brand-text">{t.deniedTitle}</h1>
      <p className="text-brand-text-muted max-w-md mb-2">{t.deniedBody}</p>
      {permission && <p className="mb-6 font-mono text-xs text-brand-text-muted">{t.requires} {permission}</p>}
      <Button asChild variant="outline">
        <Link to="/app">{t.backHome}</Link>
      </Button>
    </div>
  )
}

function LockedRoutePage() {
  return (
    <div className="flex min-h-[60vh] flex-col items-center justify-center text-center px-4">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-surface mb-6">
        <Lock className="h-8 w-8 text-brand-text-muted" />
      </div>
      <h1 className="text-2xl font-bold mb-2">Feature locked</h1>
      <p className="text-brand-text-muted max-w-md mb-6">
        This feature is not available on your current plan. Upgrade to get access.
      </p>
      {isModuleEnabled('billing') && (
        <Button asChild>
          <Link to="/app/billing">View plans</Link>
        </Button>
      )}
    </div>
  )
}

/**
 * @typedef {{ children?: React.ReactNode }} RouteErrorBoundaryProps
 * @typedef {{ hasError: boolean, error: Error | null }} RouteErrorBoundaryState
 */

/**
 * Error boundary that catches crashes inside protected route children.
 * Instead of showing a blank white page (React error #130), it shows
 * a recovery UI with a retry button and a link back to /auth.
 * @extends {Component<RouteErrorBoundaryProps, RouteErrorBoundaryState>}
 */
class RouteErrorBoundary extends Component {
  /** @param {RouteErrorBoundaryProps} props */
  constructor(props) {
    super(props)
    /** @type {RouteErrorBoundaryState} */
    this.state = { hasError: false, error: null }
  }

  /** @param {Error} error */
  static getDerivedStateFromError(error) {
    return { hasError: true, error }
  }

  /**
   * @param {Error} error
   * @param {React.ErrorInfo} errorInfo
   */
  componentDidCatch(error, errorInfo) {
    console.error('[ProtectedRoute] child component crashed:', error, errorInfo)
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null })
  }

  handleSignOut = () => {
    // Navigate to auth — use window.location so we fully reset React state
    window.location.href = '/auth'
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex min-h-[60vh] flex-col items-center justify-center text-center px-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[var(--color-error-bg)] mb-6">
            <AlertTriangle className="h-8 w-8 text-[var(--color-error)]" />
          </div>
          <h1 className="text-2xl font-bold mb-2">Something went wrong</h1>
          <p className="text-brand-text-muted max-w-md mb-6">
            This page failed to load. Try refreshing, or sign in again if the problem persists.
          </p>
          <div className="flex gap-3">
            <Button onClick={this.handleRetry} variant="default" className="gap-2">
              <RefreshCw className="h-4 w-4" />
              Try again
            </Button>
            <Button onClick={this.handleSignOut} variant="outline">
              Sign in again
            </Button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

/**
 * Route guard: session → onboarding → role → permission → plan lock.
 * @param {Object} props
 * @param {import('react').ReactNode} props.children
 * @param {string} [props.role] - Minimum role (e.g. 'admin'); higher roles such as `owner` pass too
 * @param {string} [props.permission] - Permission key from the role matrix (e.g. 'orders.read')
 */
export function ProtectedRoute({ children, role, permission }) {
  const { user, loading, isAuthenticated } = useAuthContext()
  const perms = usePermissions()
  const location = useLocation()

  if (loading || (permission && isAuthenticated && perms.loading)) {
    return (
      <div className="flex h-screen items-center justify-center">
        <Spinner />
      </div>
    )
  }

  if (!isAuthenticated) {
    return <Navigate to="/auth" state={{ from: location }} replace />
  }

  // Redirect new users to /onboarding before they reach the rest of the app.
  // Skip when already there to avoid a redirect loop. (The old target,
  // /app/claim-team, was a product-specific route that does not exist here.)
  // Only when the onboarding feature module is on — otherwise /onboarding is
  // not routed and every user would bounce into the 404 page.
  if (isModuleEnabled('onboarding') && user && user.onboardingCompleted === false && !location.pathname.startsWith('/onboarding')) {
    return <Navigate to="/onboarding" replace />
  }

  // Legacy role gate for the upstream SaaS admin pages: `owner` outranks `admin`.
  if (role && !roleSatisfies(user?.role, role)) {
    return <Navigate to="/app" replace />
  }

  // Permission gate for the store back office — mirrors requirePermission() on the API.
  if (permission && !perms.can(permission)) {
    return <AccessDeniedPage permission={permission} />
  }

  // Subscription-based route gating via isRouteLocked()
  const userPlan = user?.subscription?.plan ?? user?.plan ?? null
  if (isRouteLocked(location.pathname, userPlan)) {
    return <LockedRoutePage />
  }

  return (
    <>
      <EmailVerificationBanner />
      <RouteErrorBoundary>{children}</RouteErrorBoundary>
    </>
  )
}
